import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { useTranslation } from '@lyearn/i18n';
import { FileTextFill } from '@lyearn/icons';
import { Box, CircularProgress, Stack, Typography } from '@lyearn/ui';
import { ProgressSize } from '@lyearn/ui/dist/esm/Progress/types';
export type SerializedUploadPlaceholderNode = Spread<
  {
    filename: string;
    progress: number;
    type: 'uploadPlaceholder';
    version: 1;
  },
  SerializedLexicalNode
>;

export class UploadPlaceholderNode extends DecoratorNode<JSX.Element> {
  __filename: string;
  __progress: number;

  static getType(): string {
    return 'uploadPlaceholder';
  }

  static clone(node: UploadPlaceholderNode): UploadPlaceholderNode {
    return new UploadPlaceholderNode(node.__filename, node.__progress, node.__key);
  }

  constructor(filename: string, progress: number = 0, key?: NodeKey) {
    super(key);
    this.__filename = filename;
    this.__progress = progress;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    const theme = config.theme;
    const className = theme.uploadPlaceholder;
    if (className) {
      div.className = className;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return null;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.textContent = `Uploading ${this.__filename}...`;
    return { element };
  }

  setProgress(progress: number): void {
    const writable = this.getWritable();
    writable.__progress = progress;
  }

  decorate(): JSX.Element {
    return <UploadPlaceholderComponent filename={this.__filename} progress={this.__progress} />;
  }

  static importJSON(serializedNode: SerializedUploadPlaceholderNode): UploadPlaceholderNode {
    const node = $createUploadPlaceholderNode(serializedNode.filename, serializedNode.progress);
    return node;
  }

  exportJSON(): SerializedUploadPlaceholderNode {
    return {
      ...super.exportJSON(),
      filename: this.__filename,
      progress: this.__progress,
      type: 'uploadPlaceholder',
      version: 1,
    };
  }
}

function UploadPlaceholderComponent({
  filename,
  progress,
}: {
  filename: string;
  progress: number;
}) {
  const { t } = useTranslation();
  return (
    <Box
      aria-label={`Uploading ${filename}, ${progress}% complete`}
      className="relative h-64 mx-auto items-center shadow-02 hover:shadow-03 w-[80%] my-4 p-4 px-16 py-8 bg-interactive-primary-active rounded-lg rounded-6"
      role="status">
      <Stack alignItems="center" direction="row" gapX={3}>
        <FileTextFill className="w-20 h-20 text-gray-500 mr-16" fillColorToken="icon-white" />
        <Stack className="flex-1" direction="col" gapX={1}>
          <Typography className="text-bg-primary" numberOfLines={1} variant="body-short-01">
            {filename}
          </Typography>
          <Typography className="text-bg-primary" numberOfLines={1} variant="body-short-02">
            {t('Common:UploadProgress', { progress })}
          </Typography>
        </Stack>
        <CircularProgress
          className="text-text-placeholder"
          color="success"
          size={'medium' as ProgressSize}
          value={progress}
        />
      </Stack>
    </Box>
  );
}

export function $createUploadPlaceholderNode(
  filename: string,
  progress: number = 0,
): UploadPlaceholderNode {
  return $applyNodeReplacement(new UploadPlaceholderNode(filename, progress));
}
